import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import Layout from "../../components/Layout.tsx";
import Title from "../../components/Title.tsx";
import Button from "../../components/Button.tsx";
import {
  deleteRole,
  getRoleById,
  roleApiError,
  updateRole,
  updateRoleStatus,
} from "../../services/role.service.js";
import { notification } from "../../utils/Notifications.tsx";
import { can } from "../../auth/can.ts";
import { useAppSettings } from "../../hooks/useAppSettings.tsx";
import RolePermissionEditor from "./RolePermissionEditor.tsx";

const RoleDetail = () => {
  useAppSettings();
  const { id } = useParams();
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.auth.user);
  const [role, setRole] = React.useState<any>(null);
  const [name, setName] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [loading, setLoading] = React.useState(true);

  const canEdit = can("roles.edit", user);
  const canDeactivate = can("roles.deactivate", user);
  const canDelete = can("roles.delete", user);
  const canAssignPermissions = can("roles.assign_permissions", user);

  const load = async () => {
    setLoading(true);
    try {
      const response = await getRoleById(Number(id));
      setRole(response);
      setName(response.name);
      setDescription(response.description || "");
    } catch (error) {
      notification("Error", roleApiError(error), "danger");
      navigate("/roles");
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const save = async () => {
    try {
      const updated = await updateRole(role.id, { name, description });
      setRole(updated);
      notification("Éxito", "Rol actualizado", "success");
    } catch (error) {
      notification("Error", roleApiError(error), "danger");
    }
  };

  const toggleStatus = async () => {
    try {
      const updated = await updateRoleStatus(role.id, !role.isActive);
      setRole(updated);
      notification("Éxito", updated.isActive ? "Rol activado" : "Rol desactivado", "success");
    } catch (error) {
      notification("Error", roleApiError(error), "danger");
    }
  };

  const remove = async () => {
    if (!window.confirm("¿Eliminar este rol personalizado?")) return;
    try {
      await deleteRole(role.id);
      notification("Éxito", "Rol eliminado", "success");
      navigate("/roles");
    } catch (error) {
      notification("No se pudo eliminar", roleApiError(error), "danger");
    }
  };

  if (!role) {
    return (
      <Layout loading={loading}>
        <Title baseTitle="Roles" basePath="/roles" title="Rol" />
      </Layout>
    );
  }

  const isSystem = role.isSystem === true;
  const nameReadOnly = isSystem || !canEdit;

  return (
    <Layout loading={loading}>
      <Title
        baseTitle="Roles"
        basePath="/roles"
        activeTitle={role.name}
        title={role.name}
      />
      <div className="card border-0 m-4 p-4">
        <div className="mb-3">
          {isSystem ? (
            <span className="badge bg-secondary me-2">Sistema</span>
          ) : (
            <span className="badge bg-info me-2">Personalizado</span>
          )}
          {role.isActive ? (
            <span className="badge bg-success">Activo</span>
          ) : (
            <span className="badge bg-warning text-dark">Inactivo</span>
          )}
        </div>
        <div className="mb-3">
          <label className="form-label">Nombre</label>
          <input
            className="form-control"
            value={name}
            readOnly={nameReadOnly}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="mb-3">
          <label className="form-label">Code</label>
          <input className="form-control" value={role.code || ""} readOnly />
        </div>
        <div className="mb-3">
          <label className="form-label">grantsAllPermissions</label>
          <input
            className="form-control"
            value={role.grantsAllPermissions ? "true" : "false"}
            readOnly
          />
        </div>
        <div className="mb-3">
          <label className="form-label">Usuarios asignados</label>
          <input
            className="form-control"
            value={role.assignedUsersCount ?? 0}
            readOnly
          />
        </div>
        <div className="mb-3">
          <label className="form-label">Permisos asignados</label>
          <input
            className="form-control"
            value={role.permissionsCount ?? 0}
            readOnly
          />
        </div>
        <div className="mb-3">
          <label className="form-label">Descripción</label>
          <textarea
            className="form-control"
            value={description}
            readOnly={nameReadOnly}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
        <div className="row justify-content-end g-2">
          <div className="col-md-3">
            <Button
              type="button"
              variant="secondary"
              title="Volver"
              onClick={() => navigate("/roles")}
            />
          </div>
          {!isSystem && canEdit && (
            <div className="col-md-3">
              <Button type="button" variant="azul" title="Guardar" onClick={save} />
            </div>
          )}
          {!isSystem && canDeactivate && (
            <div className="col-md-3">
              <Button
                type="button"
                variant="warning"
                title={role.isActive ? "Desactivar" : "Activar"}
                onClick={toggleStatus}
              />
            </div>
          )}
          {!isSystem && canDelete && (
            <div className="col-md-3">
              <Button type="button" variant="danger" title="Eliminar" onClick={remove} />
            </div>
          )}
        </div>
      </div>
      <div className="card border-0 m-4 p-4">
        <h5>Permisos y alcance</h5>
        <RolePermissionEditor
          roleId={role.id}
          canEdit={canAssignPermissions && role.grantsAllPermissions !== true}
        />
      </div>
    </Layout>
  );
};

export default RoleDetail;
